<template>
  <div class="pad">
    <b-row :class="'hidkey_' + hotel.id">
      <b-col class="pad__column pad__column--first" cols="12" lg="3">
        <custom-link v-bind="detail_custom_link">
          <template #content>
            <div
              class="pad__image"
              :style="'background-image:url('+ hotel.thumbnail_url +'), url(\'/assets/images/common/statics/room_placeholder.png\')'"
            >
              <span class="pad__image-corner"></span>
            </div>
          </template>
        </custom-link>
      </b-col>
      <b-col class="pad__column pad__column--second" cols="12" lg="9">
        <div class="pad__wrap" :class="!amenities_flags.length && 'pad__wrap--20'">
          <div class="pad__row pad__row--first">
            <div class="pad__info">
              <div class="pad__head">
                <custom-link v-bind="detail_custom_link">
                  <template #content>
                    <h2 class="pad__head-name">{{ hotel.name }}</h2>
                  </template>
                </custom-link>
                <div class="pad__head-star" v-if="hotel.ratings.property">
                  <b-form-rating
                    no-border inline readonly
                    size="sm"
                    variant="secondary"
                    :value="hotel.ratings.property.rating"
                  />
                </div>
                <p class="pad__head-category" v-if="get_categories(hotel.salabam_category_id)">
                  {{ get_categories(hotel.salabam_category_id) }}
                </p>
              </div>
              <div v-if="hotel.location && hotel.location.proximity_distance" class="pad__address">
                <span class="pad__address-map" @click="$emit('toggle-map')">
                  <vb-icon :name="'pin'" :color="sass('primary')" :size="12" class="mr-1" />
                  {{hotel.location.city}}
                </span>
                <span class="pad__address-proximity" v-b-popover.hover.right="'La distanza è in linea d’aria. Il percorso reale potrebbe essere diverso.'">
                  <vb-icon :name="'flag'" :color="sass('secondary')" :size="12" class="mr-1" />
                  <span v-if="location.name">
                    {{ round(hotel.location.proximity_distance, 1) }} km dal centro di {{ last_search_location.name }}
                  </span>
                  <span v-else>distanza {{ round(hotel.location.proximity_distance, 1) }} km </span>
                  <vb-icon class="ml-1" :name="'info'" :color="sass('secondary')" :size="14" />
                </span>
              </div>
            </div>
            <score
              class="pad__score"
              :direction="'row'"
              :rating="hotel.ratings.salabam_score"
            />
          </div>
          <div class="pad__row pad__row--second">
            <div class="pad__room">
              <rate-promotions
                :promos="hotel.room.rate.promotions"
                :show="'value_adds'"
              />
              <p class="pad__room-title">{{ hotel.room.name }}</p>
              <rate-info
                class="pad__room-info"
                :rate="hotel.room.rate"
              />
            </div>
            <div class="pad__price">
              <rate-promotions
                :promos="hotel.room.rate.promotions"
              />
              <rate-amount
                :prices="hotel.room.rate.pricing"
              />
              <custom-link v-bind="detail_custom_link">
                <template #content>
                  <vb-button
                    class="pad__cta shadow"
                    :vb_options="{
                      text: 'Vedi disponibilità',
                      variant: 'info',
                      size: 'sm'
                    }"
                  />
                </template>
              </custom-link>
            </div>
          </div>
          <div class="pad__push" v-if="amenities_flags.length">
            <template v-for="(amenity, amenity_index) in amenities_flags">
              <div
                class="pad__push-btn"
                v-if="amenity_index < 2"
                :key="_uid+'amenities'+amenity+amenity_index"
              >
                <amenity
                  v-b-popover.hover.top="get_amenities_flags(amenity).label"
                  :item="amenity"
                  :type="'flags'"
                />
              </div>
            </template>
            <div
              class="pad__push-btn"
              v-b-modal="_uid+'modal_amenities'"
              v-if="amenities_flags.length > 2"
            >
              <vb-icon
                class="pad__push-icon"
                :name="'dotted'"
                :color="sass('black')"
                :size="16"
              />
            </div>
            <vb-modal
              :vb_options="{
                id: _uid+'modal_amenities',
                body_class: 'pad__modal',
                title: 'Principali Servizi',
                size: 'md',
                centered: true,
                hide_footer: true
              }"
            >
              <div class="pad__modal-wrap">
                <template v-for="(amenity, amenity_index) in amenities_flags">
                  <div class="pad__modal-item" v-if="amenity" :key="_uid+'amenities'+amenity_index">
                    <amenity
                      :item="amenity"
                      :type="'flags'"
                    />
                  </div>
                </template>
              </div>
            </vb-modal>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { round } from 'mathjs'
import { get_amenities_flags, get_categories } from '@liveeps/resources'
import { decode_parameters } from '@utilities'
import { mapState } from 'vuex'
import Score from "@liveeps/components/atoms/score.vue"
import Amenity from "@liveeps/components/atoms/amenity"
import RateInfo from "@liveeps/components/atoms/rate-info"
import RateAmount from "@liveeps/components/blocks/rate-amount"
import RatePromotions from "@liveeps/components/blocks/rate-promotions"

export default {
  name: "pad",

  components: {
    'score': Score,
    'amenity': Amenity,
    'rate-info': RateInfo,
    'rate-amount': RateAmount,
    'rate-promotions': RatePromotions,
  },

  props: {
    hotel: Object,
  },

  data() {
    return {
      last_search_location: ''
    }
  },

  provide() {
    return {
      prices: () => this.hotel.room.rate.pricing // injection per rate-deal
    }
  },

  computed: {
    // module state keys
    ...mapState(
      'liveeps', [
        'location',
        'current_search',
      ]
    ),

    amenities_flags() {
      const { amenities_flags } = this.hotel
      return Object.keys(amenities_flags).filter(key => amenities_flags[key])
    },

    detail_custom_link() {
      return {
        before_navigation: () => {
          this.$store.commit('liveeps/update_selected_property', this.hotel)
        },
        route_name: 'detail',
        css_class: 'd-block h-100',
        as_default: this.get_config('guiSettings.openDetailNewTab', false),
      }
    },
  },

  created() {
    this.get_amenities_flags = get_amenities_flags
    this.get_categories = get_categories
    this.round = round
    this.decode_parameters = decode_parameters
  },

  mounted() {
    this.last_search_location = decode_parameters(this.current_search).suggestion
  },

  watch: {},

  methods: {
  },
}
</script>

<style lang="scss" scoped>

  %deco_circle {  //todo : spostare in utilities sass
    &:after {
      content: '';
      width: 4px;
      height: 4px;
      display: inline-block;
      border-radius: $border-radius;
      background-color: $gray-600;
      margin-left: $grid-gutter-width/5;
    }

    &:last-child {
      margin-right: 0;

      &:after {
        display: none;
      }
    }
  }

  .pad {
    $this: &;
    position: relative;

    &__column {

      &--first {
        @at-root #{$this}__wrap & {
          @include media-breakpoint-up(lg) {
            padding-left: 0;
          }
        }

        .custom-link {
          height: 100%;
        }
      }

      &--first, &--second {
        position: static !important;
      }
    }

    &__image {
      min-height: 300px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: cover;
      border-radius: $border-radius-lg * 2;
      clip-path: polygon(0% 0%, 100% 0%, 100% 102%, 85% 84%, 0 84%);
      position: relative;
      z-index: 1;

      @include media-breakpoint-up(lg) {
        clip-path: polygon(0 0, 101% 0, 91% 23%, 91% 100%, 0% 101%);
        min-height: 100%;
      }

      // trick angolo smussato
      &-corner {
        width: 30px;
        height: 30px;
        overflow: hidden;
        position: absolute;
        left: 0;
        bottom: 16%;
        z-index: 1;
        transform: scaleX(-1);

        @include media-breakpoint-up(lg) {
          left: auto;
          right: 9%;
          bottom: 0;
          transform: scaleX(1);
        }

        &:before {
          content: "";
          width: 200%;
          height: 200%;
          position: absolute;
          border-radius: $border-radius-lg * 2;
          bottom: 0;
          right: 0;
          box-shadow: 50px 50px 0 0 $vb-body-bg;
        }
      }
    }

    &__wrap {
      background-color: $white;
      margin-top: -40px;
      padding: 15px 20px;
      border-radius: $border-radius-lg * 2;
      display: flex;
      flex-flow: column;

      @include media-breakpoint-up(lg) {
        margin-top: 0;
        margin-left: -40px;
        padding: 20px 60px 20px 20px;
        position: relative;
      }

      &--20 {
        @include media-breakpoint-up(lg){
          padding-right: 20px;
        }
      }
    }

    &__row {
      display: flex;
      flex-direction: column;

      @include media-breakpoint-up(md){
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }

      &--second {
        align-items: flex-start;
        margin-top: $grid-gutter-width / 2;
        order: 2;
      }
    }

    &__head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      @include media-breakpoint-up(lg) {
        flex-wrap: nowrap;
        position: relative;
      }

      &-name {
        font-size: $font-size-lg;
        font-weight: $vb-font-weight-semibold;
        margin-bottom: 0;
        margin-right: 5px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 250px;
        position: relative;
        padding-left: $grid-gutter-width / 2;

        &:before {
          content: '';
          width: $grid-gutter-width / 5;
          background-color: var(--secondary);
          position: absolute;
          top: 3px;
          bottom: 3px;
          left: 0;
          border-radius: $border-radius;
        }
      }

      &-star {
        display: flex;
        margin-right: 5px;
      }

      &-category {
        color: $gray-600;
        font-size: $font-size-sm;
        position: absolute;
        z-index: 1;
        top: 10px;
        left: 10px;
        background-color: $white;
        padding: 4px 5px;
        border-radius: $border-radius;
        margin-bottom: 0;

        @include media-breakpoint-up(lg){
          position: static;
          background-color: transparent;
          padding: 0;
          border-radius: 0;

          &:before {
            content: "("
          }
          &:after {
            content: ")"
          }
        }
      }
    }

    &__address {
      font-size: $font-size-md;

      @include media-breakpoint-up(md){
        display: flex;
        align-items: center;
      }

      &-map, &-proximity {
        display: flex;
        align-items: center;
      }

      &-map {
        font-weight: $vb-font-weight-semibold;
        color: var(--info);
        cursor: pointer;
      }

      &-proximity {
        color: var(--secondary);

        @include media-breakpoint-up(md){
          margin-left: $grid-gutter-width/3;
        }
      }
    }

    &__score {
      justify-content: flex-end;

      @include media-breakpoint-up(lg) {
        position: static;
      }
    }

    &__room {
      padding-left: $grid-gutter-width/3;
      border-left: 1px solid $gray-300;

      @include media-breakpoint-up(lg){
        flex-basis: 70%;
      }

      &-title {
        font-size: $font-size-md;
        font-weight: $vb-font-weight-semibold;
        background-color: $gray-200;
        color: $gray-600;
        padding: 0 5px;
        border-radius: 5px;
        margin-bottom: 0;
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 250px;

        @include media-breakpoint-up(lg){
          max-width: 350px;
        }
      }

      &-info {
        border: none;
        padding-left: 0;
      }
    }

    &__price {
      line-height: normal;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-direction: column;
      width: 100%;
      margin-top: $grid-gutter-width / 2;
      position: relative;

      @include media-breakpoint-up(md) {
        text-align: right;
        flex-basis: 40%;
        display: block;
        margin-top: -$grid-gutter-width / 3;
      }
    }

    &__cta {
      font-weight: $vb-font-weight-semibold;
      font-size: $font-size-md;
      margin-top: $grid-gutter-width / 4;
    }

    &__push {
      display: flex;
      align-items: center;
      padding: $grid-gutter-width / 3 0;

      @include media-breakpoint-up(lg) {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        background-color: var(--primary);
        padding: 0 7px;
        margin-top: 0;
        flex-direction: column;
        justify-content: center;
        border-radius: $border-radius-lg * 2;
        overflow: hidden;
      }

      &-btn {
        background-color: var(--primary);
        border-radius: $rounded-pill;
        padding: 10px;
        margin-right: 10px;

        @include media-breakpoint-up(lg) {
          background-color: transparent;
          border-radius: 0;
          padding: 10px 0 0;
          margin-right: 0;
          margin-top: 10px;
          border-top: 1px solid rgba(255,255,255, .20);
        }

        &:first-child {
          @include media-breakpoint-up(lg) {
            margin-top: 0;
            padding-top: 0;
            border-top: 0;
          }
        }

        &:last-child {
          margin-right: 0;
        }

        ::v-deep {
          // cambio colore icona desktop
          svg * {
            fill: $white;
          }

          .amenity {

            &__icon {
              margin: 0;
            }
            &__text {
              display: none;
            }
          }
        }
      }
    }

    &__modal {

      &-wrap {
        display: flex;
        flex-wrap: wrap;
      }

      &-item {
        display: flex;
        align-items: center;
        flex-basis: 33.333%;
        margin: $grid-gutter-width / 6 0;

        &_icon {
          margin-right: 5px;
        }
      }
    }
  }

</style>